import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  devLog,
  isSessionStorageAvailable,
  isValidStravaUrlForCurrentHost
} from '@strava/utils';

export type SignUpModalVariants =
  | 'generic'
  | 'generic_time_delayed'
  | 'follow_athlete'
  | 'join_challenge'
  | 'kudos_comment'
  | 'running_tools';

export type SignUpModalContextState = {
  /**
   * used by the logged-out page to trigger opening the modal with a particular variant
   * (also sets the variant in state and handles sessionStorage if the variant
   * is one of the special redirect variants)
   */
  openSignUpModal: (variant: SignUpModalVariants) => void;
  /** used by the modal */
  isSignUpModalOpen: boolean;
  /**
   * used by the modal, also handles sessionStorage to restore the redirect if the
   * variant was one of the special redirect variants
   */
  dismissSignUpModal: () => void;
  /** used by the modal */
  variant: SignUpModalVariants;
  /**
   * used by the modal, should include any params for the variants' different
   * header and subtitle copy
   */
  copyParams: Record<SignUpModalVariants, Record<string, string>>;
  /** used by the modal, src url for the athlete avatar  */
  athleteAvatar: string;
  /** used by the modal to build the login link with the redirect param */
  defaultRedirectUrl?: string;
  /** used by the modal to prevent building the login link with the redirect param */
  useActiveAfterAuth?: boolean;
};

const SignUpModalContext = createContext<SignUpModalContextState | undefined>(
  undefined
);
SignUpModalContext.displayName = 'SignUpModalContext';

export const useSignUpModalContext = () => {
  const context = useContext(SignUpModalContext);

  // Fail fast if hook is used outside the provider
  if (!context) {
    throw new Error(
      'useSignUpModalContext must be used within the SignUpModalContext provider'
    );
  }

  return context;
};

export const SESSION_STORAGE_REDIRECT_KEY = 'redirect_to_after_signup';

type SignUpModalContextProviderProps = {
  /**
   * whatever children the context provider is wrapping
   * (ideally the parent container for whatever CTAs trigger the modal)
   */
  children: ReactNode;
  /** athlete's first name, used by the `follow_athlete` variant's copy ("Follow {Firstname}") */
  nameForFollowAthleteVariant: string;
  /** src url for the athlete avatar, used by the `follow_athlete` variant */
  athleteAvatar: string;
  /**
   * default url the user should be directed to after authentication.
   * when not provided, defaults to the current page url.
   */
  defaultRedirectUrl?: string;
  /**
   * redirect urls supercede `useActiveAfterAuth` (so a user who signs up from a
   * variant that has a redirect url will be redirected there). the url itself
   * must be an absolute url with a strava (or localhost, during dev) host.
   * @example
   *   { follow_athlete: 'https://www.strava.com/athletes/123' }
   */
  redirectUrls?: Record<string, string>;
  /**
   * when true, does not use the default redirect url (if provided) or set
   * the current page as the default redirect url (if default redirect url is
   * not provided). defaults to false.
   */
  useActiveAfterAuth?: boolean;
  /** called when the sign up modal is dismissed */
  onDismiss?: () => void;
};

export const SignUpModalContextProvider = ({
  children,
  nameForFollowAthleteVariant,
  athleteAvatar,
  redirectUrls = {}, // redirect urls supercede `useActiveAfterAuth`
  defaultRedirectUrl: _defaultRedirectUrl,
  useActiveAfterAuth = false,
  onDismiss = () => {}
}: SignUpModalContextProviderProps) => {
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [defaultRedirectUrl, setDefaultRedirectUrl] = useState(
    _defaultRedirectUrl || ''
  );
  const [variant, setVariant] = useState<SignUpModalVariants>('generic');

  const sessionStorageIsAvailable = useMemo(
    () => isSessionStorageAvailable(),
    []
  );

  const redirectUrlIsValid = useCallback(
    (redirectUrl) =>
      isValidStravaUrlForCurrentHost(
        redirectUrl,
        new URL(window?.location?.href).host
      ),
    []
  );

  const setSessionStorageRedirectUrl = useCallback(
    (redirectUrl) => {
      if (sessionStorageIsAvailable) {
        if (redirectUrlIsValid(redirectUrl)) {
          sessionStorage.setItem(SESSION_STORAGE_REDIRECT_KEY, redirectUrl);
          devLog(`SUM :: setting sessionStorage with ${redirectUrl}`);
        } else {
          devLog(`SUM :: ${redirectUrl} is an invalid redirect url`);
        }
      }
    },
    [redirectUrlIsValid, sessionStorageIsAvailable]
  );

  // on load, use the default redirect url if it's provided and valid,
  // otherwise use the current page; do nothing if we're using active's after
  // auth handling
  useEffect(() => {
    if (!useActiveAfterAuth) {
      const redirectUrl = redirectUrlIsValid(defaultRedirectUrl)
        ? defaultRedirectUrl
        : window?.location?.href;
      setSessionStorageRedirectUrl(redirectUrl);
      setDefaultRedirectUrl(redirectUrl);
    }
  }, [
    defaultRedirectUrl,
    redirectUrlIsValid,
    setSessionStorageRedirectUrl,
    useActiveAfterAuth
  ]);

  const openSignUpModal = useCallback(
    (_variant: SignUpModalVariants) => {
      setIsSignUpModalOpen(true);
      setVariant(_variant);

      // if variant has a special redirect, set it in session storage
      if (Object.keys(redirectUrls).includes(_variant)) {
        setSessionStorageRedirectUrl(redirectUrls[_variant]);
      }
    },
    [redirectUrls, setSessionStorageRedirectUrl]
  );

  const dismissSignUpModal = useCallback(() => {
    onDismiss();
    setIsSignUpModalOpen(false);

    // restore default redirect url unless we're using not using an after auth redirect
    if (Object.keys(redirectUrls).includes(variant)) {
      if (useActiveAfterAuth) {
        sessionStorage.removeItem(SESSION_STORAGE_REDIRECT_KEY);
      } else {
        setSessionStorageRedirectUrl(defaultRedirectUrl);
      }
    }
  }, [
    defaultRedirectUrl,
    onDismiss,
    redirectUrls,
    setSessionStorageRedirectUrl,
    useActiveAfterAuth,
    variant
  ]);

  const providerState = useMemo(
    () => ({
      openSignUpModal,
      dismissSignUpModal,
      isSignUpModalOpen,
      variant,
      copyParams: {
        generic: {},
        generic_time_delayed: {},
        follow_athlete: { name: nameForFollowAthleteVariant },
        join_challenge: {},
        kudos_comment: {},
        running_tools: {}
      },
      athleteAvatar,
      defaultRedirectUrl,
      useActiveAfterAuth
    }),
    [
      openSignUpModal,
      dismissSignUpModal,
      isSignUpModalOpen,
      variant,
      nameForFollowAthleteVariant,
      athleteAvatar,
      defaultRedirectUrl,
      useActiveAfterAuth
    ]
  );

  return (
    <SignUpModalContext.Provider value={providerState}>
      {children}
    </SignUpModalContext.Provider>
  );
};
